// The LM lane-restriction view (Orbis spec 12.18, `vehicle_restriction:<mode>` and
// `hov_lane*`): per mode what is tagged and the result, read with lmAccess so the
// table agrees with the map's colour.
import {
  accessClassOf,
  lmAccess,
} from "../layers/style/util/restrictionClass.mjs";
import { baseKey } from "./lrValues.mjs";
import { buildStretches, tagSections } from "./stretches.mjs";

// Most general first; a more specific readable token overrides (lmAccess).
export const LM_HIERARCHY = [
  "motor_vehicle",
  "psv",
  "bus",
  "hgv",
  "motorcar",
  "taxi",
];
const FIELDS = {
  motor_vehicle: "vehicleRestrictionMotorVehicle",
  psv: "vehicleRestrictionPSV",
  bus: "vehicleRestrictionBus",
  hgv: "vehicleRestrictionHGV",
  motorcar: "vehicleRestrictionMotorcar",
  taxi: "vehicleRestrictionTaxi",
};
const MODES = ["bus", "hgv", "motorcar", "taxi"];
const HOV_KEYS = [
  "hov_lane",
  "hov_lane:conditional",
  "hov_lane:minimum",
  "hov_lane:minimum:conditional",
  "hov_lane:clean_fuel",
  "hov_lane:clean_fuel:conditional",
];
const tokenKey = (token) => `vehicle_restriction:${token}`;

/**
 * @param {import("./featureModel.mjs").FeatureModel} model
 */
export function buildLmRestrictionView(model) {
  const viewKeys = [...LM_HIERARCHY.map(tokenKey), ...HOV_KEYS];
  const tagsByKey = new Map();
  model.groups.forEach((group) =>
    group.tags.forEach((tag) => {
      const key = baseKey(tag.key);
      if (viewKeys.includes(key) && !tagsByKey.has(key) && tagSections(tag))
        tagsByKey.set(key, tag);
    }),
  );
  if (tagsByKey.size === 0) return null;

  const sectionsByKey = Object.fromEntries(
    [...tagsByKey].map(([key, tag]) => [key, tagSections(tag)]),
  );
  const stretches = buildStretches(sectionsByKey, model.lengthCm).map(
    ({ startCm, endCm, values }) => {
      const tokenValue = (token) => values[tokenKey(token)];
      const access = lmAccess(
        Object.fromEntries(
          LM_HIERARCHY.map((token) => [FIELDS[token], tokenValue(token)]),
        ),
      );
      const modes = MODES.map((mode) => ({
        mode,
        tagged: tokenValue(mode),
        result: access[mode],
      }));
      return {
        startCm,
        endCm,
        modes,
        general: ["motor_vehicle", "psv"]
          .filter((token) => tokenValue(token) !== undefined)
          .map((token) => ({ token, value: tokenValue(token) })),
        hov: HOV_KEYS.filter((key) => values[key] !== undefined).map((key) => ({
          key,
          value: values[key],
        })),
        className: accessClassOf(access),
      };
    },
  );
  return { stretches, usedTags: [...tagsByKey.values()] };
}
